import { FC } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import badgeIcon from '../../assets/images/badge--icon.svg';
import clockGrayIcon from '../../assets/images/clock--gray.svg';
import inProgressIcon from '../../assets/images/in-progress-icon.svg';
import actionRequiredIcon from '../../assets/images/action-required-icon.svg';
import completedIcon from '../../assets/images/completed-icon.svg';
import timeIcon from '../../assets/images/time-icon.svg';
import editNoteIcon from '../../assets/images/edit-note.svg';
import uncertifiedIcon from '../../assets/images/uncertified__icon.svg';
import speakingUnscorableIcon from '../../assets/images/speaking-unscorable-icon.svg';
import notStartedIcon from '../../assets/images/not_started__icon.svg';
import ViolationIcon from '../../assets/images/violation-icon.svg';
import {
  statusBadgeActionRequired,
  statusBadgeAppealed,
  statusBadgeCertified,
  statusBadgeCompleted,
  statusBadgeExpired,
  statusBadgeInProgress,
  statusBadgeLateHangup,
  statusBadgeNotStarted,
  statusBadgeSpeakingUnscorable,
  statusBadgeStarted,
  statusBadgeUncertified
} from './StatusBadge.styles';
import { License, LicenseStatus } from '../../interface/licenses.interfaces';
import { useTranslation } from 'react-i18next';
import { Img } from '../Primitive';

export interface IStatusBadgeProps {
  license?: License;
}

interface StatusBadgeContentProps {
  sx: SxProps<Theme>;
  icon: string;
  label: string;
}

const StatusBadgeContent: FC<StatusBadgeContentProps> = ({ sx, icon, label }) => (
  <Box sx={sx}>
    <Img src={icon} alt='' aria-hidden />
    {label}
  </Box>
);

const certifiedStyleBadgeLabels: Record<LicenseStatus.CERTIFIED | LicenseStatus.FINALIZED, string> =
  {
    [LicenseStatus.CERTIFIED]: 'results.resultBadge.certified',
    [LicenseStatus.FINALIZED]: 'results.resultBadge.finalized'
  };

const useGetBadge = (license?: License) => {
  const { t } = useTranslation();

  if (!license) return;

  const { status, requiresUserAction } = license;

  if (!status) return;

  if (requiresUserAction) {
    return (
      <StatusBadgeContent
        sx={statusBadgeActionRequired}
        icon={actionRequiredIcon}
        label={t('results.resultBadge.actionRequired')}
      />
    );
  }

  switch (status) {
    case LicenseStatus.CERTIFIED:
    case LicenseStatus.FINALIZED:
      return (
        <StatusBadgeContent
          sx={statusBadgeCertified}
          icon={badgeIcon}
          label={t(certifiedStyleBadgeLabels[status])}
        />
      );
    case LicenseStatus.SCORED:
      return (
        <StatusBadgeContent
          sx={statusBadgeCertified}
          icon={completedIcon}
          label={t('results.resultBadge.scored')}
        />
      );
    case LicenseStatus.CERTIFICATION_FAILED:
    case LicenseStatus.NOT_SCORABLE:
    case LicenseStatus.PARTIALLY_SCORED:
    case LicenseStatus.LATE_HANGUP:
    case LicenseStatus.USER_HUNG_UP:
    case LicenseStatus.ERROR:
      return (
        <StatusBadgeContent
          sx={statusBadgeUncertified}
          icon={uncertifiedIcon}
          label={t('results.resultBadge.uncertified')}
        />
      );
    case LicenseStatus.CERTIFICATION_REVOKED:
      return (
        <StatusBadgeContent
          sx={statusBadgeUncertified}
          icon={uncertifiedIcon}
          label={t('results.resultBadge.revoked')}
        />
      );
    case LicenseStatus.SPEAKING_UNSCORBALE:
      return (
        <StatusBadgeContent
          sx={statusBadgeSpeakingUnscorable}
          icon={speakingUnscorableIcon}
          label={t('results.resultBadge.speakingUnscorable')}
        />
      );
    case LicenseStatus.APPEALED:
      return (
        <StatusBadgeContent
          sx={statusBadgeAppealed}
          icon={clockGrayIcon}
          label={t('results.resultBadge.appealed')}
        />
      );
    case LicenseStatus.STARTED:
      return (
        <StatusBadgeContent
          sx={statusBadgeStarted}
          icon={editNoteIcon}
          label={t('results.resultBadge.started')}
        />
      );
    case LicenseStatus.UPLOADING:
      return (
        <StatusBadgeContent
          sx={statusBadgeInProgress}
          icon={inProgressIcon}
          label={t('results.resultBadge.uploading')}
        />
      );
    case LicenseStatus.ABORTED:
      return (
        <StatusBadgeContent
          sx={statusBadgeLateHangup}
          icon={ViolationIcon}
          label={t('results.resultBadge.ruleViolation')}
        />
      );
    case LicenseStatus.IN_PROGRESS:
      return (
        <StatusBadgeContent
          sx={statusBadgeInProgress}
          icon={inProgressIcon}
          label={t('results.resultBadge.inProgress')}
        />
      );
    case LicenseStatus.COMPLETED:
      return (
        <StatusBadgeContent
          sx={statusBadgeCompleted}
          icon={timeIcon}
          label={t('results.resultBadge.scoringInProgress')}
        />
      );
    case LicenseStatus.EXPIRED:
      return (
        <StatusBadgeContent
          sx={statusBadgeExpired}
          icon={clockGrayIcon}
          label={t('results.resultBadge.expired')}
        />
      );
    case LicenseStatus.NEW:
    case LicenseStatus.VALIDATED:
    default:
      return (
        <StatusBadgeContent
          sx={statusBadgeNotStarted}
          icon={notStartedIcon}
          label={t('results.resultBadge.notStarted')}
        />
      );
  }
};

export const StatusBadge: FC<IStatusBadgeProps> = ({ license }) => useGetBadge(license) || <></>;
